import React from 'react';
import debounce from 'lodash/debounce';
import { Grid } from 'react-virtualized/dist/commonjs/Grid';
import { WindowScroller } from 'react-virtualized/dist/commonjs/WindowScroller';
import { ColumnSizer } from 'react-virtualized/dist/commonjs/ColumnSizer';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import get from 'lodash/get';
import chunk from 'lodash/chunk';
import { InView } from 'react-intersection-observer';
import Loader from '../Loader.js';
import ProductCardComponent from './ProductCardDesktop';
import elementSize from '../../../modules/ajio-util/dom/elementSize.js';
import { setGridConfig, setGridConfigPDP } from '../../../actions/shared/productGrid.js';
import {
  loadingMoreItemsOnScroll,
  loadingMoreItemsOnScrollPDP
} from '../../../actions/shared/productsData.js';
import grid from '../../../reducers/shared/grid';
import { insertWidgetsInGrid, insertInlineFiltersInGrid } from './WidgetUtil.js';

import {
  gtmListingProductImpressions,
  luxeGtmProductImpressions,
  getStoreName,
  gtmPromotionImpressions
} from '../../../modules/ga/GTMdatalayer';
import { Feature, Variant } from '../../../refresh/organism/ABFeature';
import { getLuxeFlags } from '../../../Flags/selectors';
import { getCurrentStore } from '../../../refresh/selectors/metaData';
import {
  getPLPUnratedProductsWigetPosition,
  getWishlistUnratedProductsWidgetPosition,
  getEnableRating,
  getEnableRatingLabelPLP,
  getEnableRatingLabelWishlist,
  getShowAdTagBannerListingAds,
  getGiftWithProductEnable,
  getGiftWithProductConfig,
  getPriorityDeliveryPhase2Flag,
  getPriorityDeliverySLPTagsConfig,
  getAjiogramInterventionsConfig,
  getRushDeliveryFlag,
  getPLPProductLoadMargin,
  getUrgencyDriverV2PLPEnable,
  getPLPPerfTBTFlag,
  getVisualFiltersConfig,
  getTrendBasedDiscoveryFlag,
  getTrendBasedDiscoveryConfig
} from '../../../refresh/selectors/featureFlagsSelector';
import luxeScreens from '../../../refresh/constants/luxeScreenType';
import { loadWishlistOptionCodesAPI } from '../../../wishlist/actions/wishlist';
import { signInCheck } from '../CheckUserStatus.js';
import { getPriceRevealSaleDuration } from '../PriceRevealPLPBanner/priceRevealUtil';
import { eraseCookie, readCookie } from '../Cookie';
import { fireAddTrackers } from '../../../utils/jioAds';
import {
  getTagValueFromTagEngine,
  getPriceLableFromTagEngine,
  getPLPTagV2FromPosition
} from '../../../utils/product';
import { getFeatureVarian } from '../../../refresh/organism/ABFeature/Feature';
import { getVariant } from '../../../refresh/organism/ABFeature/Feature';
import PlpRatingWidget from '../../../refresh/molecules/PlpRatingWidget';
import { getPageInfo} from '../../../utils/pageInfo.js';
import { getDiscountValue } from '../../../utils/price';
import { redirectedFromVideoBanner, redirectedFromFloatingWidget } from '../../../utils/banner';
import properties from '../../../modules/ajio-util/properties';
import { bannerAdViewType, getUpdatedGridWithJioAds, JioAds } from './JioAds.js';
import {
  getjioAdsFrequencyMsite,
  getjioAdStartPositionMSite,
  getjioAdsEndPositionMsite,
  getjioAdsEndRowDesktop,
  getjioAdsStartRowDesktop,
  getjioAdsRowCount,
  getReleaseGA4Flag,
  getMasterGA4Flag,
  getMasterGA3Flag,
  getPlaAdsClientID
} from '../../../refresh/selectors/featureFlagsSelector';
import { fetchUnratedRatingProducts } from '../../../actions/ratings';
import { getRatingState, getUnratedItems } from '../../../refresh/selectors/ratingSelector';
import { getFeatureVariant } from '../../../refresh/organism/ABFeature/Feature';
import { InlineFilters } from '../../../refresh/organism/VisualFilter';
import { catalogIdToStoreName } from '../../../utils';
import {getValidAttributionToken, isCopilotReferrer} from '../../../utils/common.js'
import { FLEEK_STORE } from '../../../constants/constant.js';
import { getPreviousSearchSource, getPageCategory } from '../../../refresh/selectors/productpage';
// import ProductWidgetContainer from 'src/components/desktop/homepage/ProductWidgetContainer.js';
// import ProductWidgetContainer from '../../../refresh/organism/ProductsLineUp/ProductWidgetContainer';
import WidgetUI from './WidgetUI.js';
import { getAmplitudeExperimentVariant, getAmplitudeVariantPayload } from '../../../utils/amplitudeCommon.js';
import { sha256Hex } from '../../../utils/products';

// ⚡ CODE SPLITTING: Lazy load heavy modules
// Lazy load utility modules (only when feature flag is enabled)
let JioAdsManagerModule = null;
let ImpressionTrackerModule = null;
let ScrollTrackerModule = null;

// Load modules dynamically when needed (non-blocking, parallel loading)
const loadSplitModules = async () => {
  // ⚡ Load all modules in PARALLEL using Promise.all (faster than sequential await)
  const [jioAds, impressions, scroll] = await Promise.all([
    JioAdsManagerModule || import('./utils/JioAdsManager.js'),
    ImpressionTrackerModule || import('./utils/ImpressionTracker.js'),
    ScrollTrackerModule || import('./utils/ScrollTracker.js')
  ]);

  // Cache for subsequent calls
  JioAdsManagerModule = jioAds;
  ImpressionTrackerModule = impressions;
  ScrollTrackerModule = scroll;

  return {
    getBannerJioAds: jioAds.getBannerJioAds,
    sendImpressionData: impressions.sendImpressionData,
    luxeProductImpressions: impressions.luxeProductImpressions,
    luxeSendProductImpressionData: impressions.luxeSendProductImpressionData,
    createTrackImpressions: scroll.createTrackImpressions,
    createLuxeTrackImpressions: scroll.createLuxeTrackImpressions,
    createLuxeTrackDesktopImpressions: scroll.createLuxeTrackDesktopImpressions
  };
};

// ⚡ PERFORMANCE OPTIMIZATION: Memoized ProductCard wrapper
// Prevents unnecessary re-renders when props haven't changed
const ProductCard = React.memo(ProductCardComponent, (prevProps, nextProps) => {
  // Custom comparison function for better memoization
  // Only re-render if critical props change
  return (
    prevProps.id === nextProps.id &&
    prevProps.position === nextProps.position &&
    prevProps.style === nextProps.style &&
    prevProps.wishlistOptionCodeUpdates === nextProps.wishlistOptionCodeUpdates &&
    prevProps.displayRatingWidget === nextProps.displayRatingWidget &&
    prevProps.displayWidgetShimmer === nextProps.displayWidgetShimmer
  );
});
class ProductGrid extends React.Component {
  constructor(props) {
    super(props);
    this.gridContainer = null;
    this.sentinelRef = React.createRef(); // Ref for the sentinel element
    this.gridRef = React.createRef(); // Ref for the virtualized Grid component
    this.productImpressionData = {
      products: [],
      lastPushedAt: 0
    };
    this.state = {
      wishlistOptionCodeUpdates: false,
      productClicked: false,
      unratedProductsAvailable: true,
      isFetching: false
    };
    this.adbannersInViewPort = [];
    this.splitModules = null;

    // ⚡ PERFORMANCE: Cache for memoized filter computation
    this.cachedFilterSegment = null;
    this.lastFacetsHash = null;

    // ⚡ PERFORMANCE: Pre-bind methods to avoid recreation
    this.handleIntersection = this.handleIntersection.bind(this);
    this.loadMoreBound = this.loadMoreBound.bind(this);
    this.recheckSentinel = this.recheckSentinel.bind(this);

    loadSplitModules().then(modules => {
      this.splitModules = modules;
    });
  }


  /**
   * ⚡ PERFORMANCE OPTIMIZATION: Memoized filter computation (Sprint 26_01)
   * Only recomputes when facets actually change
   * TBT Reduction: ~200ms
   */
  getSelectedFilterForSegment = () => {
    const { facets = {} } = this.props;
    const { currentFacets = {} } = facets || {};
    const facetsHash = JSON.stringify(currentFacets.allfacetsItems || []);

    if (this.lastFacetsHash === facetsHash && this.cachedFilterSegment) {
      return this.cachedFilterSegment;
    }

    this.lastFacetsHash = facetsHash;
    this.cachedFilterSegment = this.computeFilterSegmentOptimized();
    return this.cachedFilterSegment;
  };

  /**
   * ⚡ OPTIMIZED: Single-pass filter computation
   * Combines all filter operations into one loop
   */
  computeFilterSegmentOptimized = () => {
    const { facets = {} } = this.props;
    const { currentFacets = {} } = facets || {};
    const { allfacetsItems = [] } = currentFacets || {};

    if (!allfacetsItems.length) return [];

    // ⚡ Single pass through facets array
    const selectedFilters = [];

    for (const facet of allfacetsItems) {
      // Handle phase1: general filters
      if (facet?.values?.some(el => el.selected)) {
        selectedFilters.push(facet);
      }

      // Handle phase2: color filters
      if (facet?.code === 'verticalcolorfamily' && facet?.colorGroups) {
        const hasSelectedColors = facet.colorGroups.some(el =>
          (el?.values || []).some(color => color.selected)
        );
        if (hasSelectedColors) {
          const values = [];
          for (const colorGroup of facet.colorGroups) {
            if (colorGroup.values) {
              values.push(...colorGroup.values.filter(c => c.selected));
            }
          }
          selectedFilters.push({ code: facet.code, name: facet.name, values });
        }
      }

      // Handle phase2: size filters
      if (facet?.code === 'verticalsizegroupformat' && facet?.l1Group) {
        const hasSelectedSizes = facet.l1Group.some(el =>
          (el?.l2Group || []).some(l2Group => (l2Group.values || []).some(size => size.selected))
        );
        if (hasSelectedSizes) {
          const values = [];
          for (const l1Group of facet.l1Group) {
            if (l1Group.l2Group) {
              for (const l2Group of l1Group.l2Group) {
                if (l2Group.values) {
                  values.push(...l2Group.values.filter(s => s.selected));
                }
              }
            }
          }
          selectedFilters.push({ code: facet.code, name: facet.name, values });
        }
      }
    }

    if (!selectedFilters.length) return [];

    // Transform to final format
    return selectedFilters.map(el => {
      const facetValue = el.values
        .filter(item => item.selected)
        .map(item => item.name)
        .join('|');
      const facetPosition = allfacetsItems.findIndex(cur => cur?.code === el?.code);

      return {
        section: 0,
        position: facetPosition,
        facet_key: el?.name || '',
        facet_value: facetValue,
        value: facetValue
      };
    });
  };

  /**
   * ⚡ CODE SPLITTING: Use split module for luxe product impressions
   * Falls back to legacy implementation if modules not loaded
   */
  luxeProductImpressions = (ids, entities, results) => {
    const { enablePlpPerformanceOptimizationsSprint2601 = false, pageCategory } = this.props;
    if (this.splitModules?.luxeProductImpressions) {
      return this.splitModules.luxeProductImpressions(ids, entities, results, {
        ...this.props,
        getSelectedFilterForSegment: this.getSelectedFilterForSegment
      });
    }

    // 🔄 LEGACY: Fallback to original implementation
    const { enableGiftWithProduct, urgencyDriverV2PLPEnable = false, deviceType = '' } = this.props;
    let { plp: { screenType = '', screenName = '' } = {} } = luxeScreens;
    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);

    const jioBannerSource = urlParams.get('jioAdsSource');
    const sourcePage = urlParams.get('sourcePage');
    const plp_source_details = jioBannerSource
      ? jioBannerSource.replaceAll('[ccb]', Date.now())
      : false;
    const { pageType: fromPage = '' } = getPageInfo(location.href);
    const isPLPSource = sourcePage || false;
    const { isWishListPage, currentStore, showPlpExtendedBanner = false, pageType } = this.props;
    let listPage = 'PLP Screen';
    const fromVideoBanner = redirectedFromVideoBanner();
    const fromFloatingWidget = redirectedFromFloatingWidget();
    let contains_luxe = false;
    if (isWishListPage) {
      screenName = luxeScreens.wishlist.screenName || '';
      screenType = luxeScreens.wishlist.screenType || '';
      listPage = 'Closet Screen';
    }
    const impressions = ids.reduce((acc, id) => {
      const product = entities[id];
      
      if (product) {
        const {
          name,
          price: { value: productPrice = '' } = {},
          wasPriceData: { value: wasProductPrice } = {},
          code = '',
          fnlColorVariantData: {
            brandName = '',
            colorGroup = '',
            exclusiveTill: isExclusive = '',
            productTag = ''
          } = {},
          fnlProductData: { planningCategory = '' } = {},
          brickName,
          sourceStoreId,
          catalog = '',
          catalogName = '',
          segmentId = '',
          experimentId = '',
          isGiftWithPurchase = false,
          offerPrice: { giftAvailable = false } = {},
          productListType = '',
          tags: { categoryTags = [] } = {},
          urgencyDriverTagResponse: { positions = [] } = {}
        } = product;

        const discount =
          Number.isInteger(wasProductPrice) && Number.isInteger(productPrice)
            ? wasProductPrice - productPrice
            : '';
        const colorValue =
          colorGroup && colorGroup?.includes('_') ? colorGroup.split('_')[1] || '' : colorGroup;
        const tag = isExclusive ? 'Exclusive' : productTag;
        let productTagEngineValue = '';
        let productPriceLabel = '';
        if (product?.tags?.categoryTags || (positions || []).length) {
          const PLPTagV2TopLeft = getPLPTagV2FromPosition(positions, 'PRODUCT_TILE_IMAGE_TOP_LEFT');
          const newProductTagEngineValue =
            deviceType === 'mobile' &&
            pageType !== 'wishlist' &&
            urgencyDriverV2PLPEnable &&
            PLPTagV2TopLeft?.shortText;

          productTagEngineValue =
            newProductTagEngineValue || getTagValueFromTagEngine(categoryTags);
          productPriceLabel = getPriceLableFromTagEngine(categoryTags);
        }
        const dimension60Data =
          Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
        let gwpDimension = '';
        if (enableGiftWithProduct) {
          if (pageType === 'wishlist' && isGiftWithPurchase) {
            gwpDimension = 'GetFreebies';
          } else if (giftAvailable) {
            gwpDimension = 'GetFreebies';
          }
        }
        acc.push({
          name,
          id: code,
          price: productPrice,
          brand: brandName,
          category: planningCategory,
          list: fromFloatingWidget
            ? 'video floating widget banner plp'
            : fromVideoBanner
            ? 'video banner plp'
            : isPLPSource || 'PLP screen', // example only,
          position: results.indexOf(id),
          dimension6: name, // pass value for product_name
          dimension7: code, // pass value for product_id
          dimension8: brandName, // pass value for product_brand
          dimension9: productPrice, // pass value for product_price
          dimension10: colorValue, // pass value for product_color
          dimension11: '', // pass value for product_size
          dimension12: discount, // pass value for product_trade_discount
          dimension13: planningCategory, // pass value for product_category
          dimension14: brickName, // pass value for product_brick
          dimension15: tag, // pass value for product_tag
          dimension16: isWishListPage ? sourceStoreId : currentStore, // pass value for Store_type
          dimension23: productListType
          ? `${catalogIdToStoreName(catalog || catalogName)}|${productListType}`
          : catalogIdToStoreName(catalog || catalogName),
          dimension60: dimension60Data,
          dimension89:
            pageType !== 'wishlist' && getVariant('enable_urgency_driver_plp')
              ? productTagEngineValue
              : '',
          dimension90:
            pageType !== 'wishlist' && getVariant('enable_urgency_driver_plp')
              ? productPriceLabel
              : '',
          dimension91: '',
          dimension82: gwpDimension,
          sticker_name: productTagEngineValue
        });
        if (isWishListPage && sourceStoreId === currentStore) {
          contains_luxe = true;
        }
      }
      return acc;
    }, []);
    luxeGtmProductImpressions({
      impressions,
      list: fromVideoBanner ? 'video banner plp' : isPLPSource || listPage,
      store_type: 'luxe',
      page_type: screenType,
      contains_luxe: isWishListPage ? contains_luxe : true,
      showPlpExtendedBanner,
      ProductArray: impressions,
      plp_source_details,
      plp_source: isPLPSource,
      bottomCardFilterValue: this.getSelectedFilterForSegment(),
      pageCategory: pageCategory
    });
  };

  /**
   * ⚡ CODE SPLITTING: Use split module for impression tracking
   * Falls back to legacy implementation if modules not loaded
   */
  sendImpressionData = (ids, entities, results) => {
    if (this.splitModules?.sendImpressionData) {
      return this.splitModules.sendImpressionData(ids, entities, results, {
        ...this.props,
        getSelectedFilterForSegment: this.getSelectedFilterForSegment
      });
    }

    // 🔄 LEGACY: Fallback to original implementation
    const {
      newDesignLuxe,
      showPlpExtendedBanner = false,
      pageType,
      pageUrl,
      toggleView,
      currentStore,
      enableGiftWithProduct,
      previousSource,
      pageCategory,
      enablePriorityDeliveryPhase2,
      enableRushDelivery,
      priorityDeliverySLPTagsConfig: { PLP_BUCKET = {} },
      ajiogramInterventionsConfig,
      urgencyDriverV2PLPEnable = false,
      deviceType = ''
    } = this.props;
    const fromVideoBanner = redirectedFromVideoBanner();
    const fromFloatingWidget = redirectedFromFloatingWidget();
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const brandPageCode = urlParams.get('brandPageCode');
    const pageRedirected = urlParams.get('ref');
    const jioBannerSource = urlParams.get('jioAdsSource');
    const sourcePage = urlParams.get('sourcePage');
    const isCuratedWidget = urlParams.get('isCuratedWidget');
    const isDynamicWidget = urlParams.get('isDynamicWidget');
    const widgetName = urlParams.get('widgetName');
    const widgetHeading = urlParams.get('heading');
    const optionName = urlParams.get('optionName');
    const jioadredirect = pageRedirected === 'banner';
    const bannerName = localStorage.getItem('bannerName');
    const cId = localStorage.getItem('campaignId');
    const { location = {} } = window || {};
    const { pageType: fromPage = '' } = getPageInfo(location.href);
    let plp_source_details = jioadredirect ? `jioads - ${bannerName} - ${cId}` : isCuratedWidget ? `curated_widget_${widgetName}_${optionName}` : isDynamicWidget ? `dynamic_${widgetHeading}` :  null;
    const fromJioAds = jioadredirect && bannerName?.length > 0 && cId?.length > 0;

    const { masterGA3, masterGA4, releaseGA4 } = this.props;
    const isGA4Feature = getFeatureVariant('isGA4Feature');

    let isGA4 = false;
    const storeName = getStoreName();
    if (isGA4Feature === '1' && releaseGA4 && masterGA4 && !masterGA3 && storeName === 'ajio') {
      isGA4 = true;
    }

    if (newDesignLuxe) {
      this.luxeProductImpressions(ids, entities, results);
    } else {
      const fleek = String(storeName).toLowerCase() === FLEEK_STORE.toLowerCase();
      const products = ids.reduce((acc, id) => {
        const product = entities[id];
        if (product) {
          // console.log("productId",{id});
          const { location: { pathname = '' } = {} } = window;
          const { pageType: pageListType } = getPageInfo(pathname);
          const {
            name,
            price: { value: productPrice = '' } = {},
            wasPriceData: { value: wasProductPrice } = {},
            code = '',
            fnlColorVariantData: {
              brandName = '',
              colorGroup = '',
              exclusiveTill = '',
              productTag = '',
              deliverySlaTag = ''
            } = {},
            fnlProductData: { planningCategory = '' } = {},
            adsData = {},
            discountPercent = '',
            averageRating,
            numUserRatings = '',
            ratingCount = '',
            catalog = '',
            catalogName = '',
            segmentId = '',
            experimentId = '',
            isGiftWithPurchase = false,
            productListType = '',
            offerPrice: { giftAvailable = false } = {},
            tags: { categoryTags = [] } = {},
            urgencyDriverTagResponse: { positions = [] } = {},
            segmentNameText='',
            brickCategory='',
            sellerId='',
          } = product;

          const {plpConfig:{ tagContent: {logo = '', text = '', backgroundColor = '', textColor = ''} = {}, enableIntervention = false} = {}}= ajiogramInterventionsConfig;

          const enablePlpIntervention = enableIntervention && String(storeName).toLowerCase() === 'ajio' &&  String(catalogName).toLowerCase() === 'ajiogram'

          let productStickerName = '';

          if(enablePlpIntervention){
            productStickerName = [productTag, text].filter(Boolean).join(",")
          } else{
            productStickerName = `${productTag}`
          }

          let productTagEngineValue = '';
          let productPriceLabel = '';
          if (product?.tags?.categoryTags || (positions || []).length) {
            const PLPTagV2TopLeft = getPLPTagV2FromPosition(
              positions,
              'PRODUCT_TILE_IMAGE_TOP_LEFT'
            );
            const newProductTagEngineValue =
              deviceType === 'mobile' &&
              pageType !== 'wishlist' &&
              urgencyDriverV2PLPEnable &&
              PLPTagV2TopLeft?.shortText;

            productTagEngineValue =
              newProductTagEngineValue || getTagValueFromTagEngine(categoryTags);
            productPriceLabel = getPriceLableFromTagEngine(categoryTags);
          }
          const { config: { c: campaignId } = {} } = adsData || {};
          const discount =
            Number.isInteger(wasProductPrice) && Number.isInteger(productPrice)
              ? wasProductPrice - productPrice
              : '';
          const colorValue =
            colorGroup && colorGroup?.includes('_') ? colorGroup.split('_')[1] || '' : colorGroup;
          let dimension59Data = '';
          const isWishlistPage = pageType?.toLowerCase() === 'wishlist';
          console.log(
            '🚀 ~ file: ProductGridDesktop.js:377 ~ ProductGrid ~ products ~ isWishlistPage:',
            isWishlistPage
          );
          if (isWishlistPage) {
            const stockInfo = product?.stockInfo || {};
            const availableQuantityData = (product?.variantOptions ?? []).reduce((acc, el) => {
              acc = el?.stock?.stockLevel ?? 0;
              return acc;
            }, 0);
            const oosReason = stockInfo?.stockErrorDescription
              ? `|${stockInfo?.stockErrorDescription}`
              : '';
            dimension59Data = `${availableQuantityData}|${
              availableQuantityData ? 'deliverable' : 'non-deliverable'
            }${oosReason}`;
          }
          const dimension60Data = Boolean(Number(segmentId) || Number(experimentId))
            ? `${segmentId}|${experimentId}`
            : '';

          let dimension166 =
            pageType !== 'wishlist' && getVariant('enable_urgency_driver_plp')
              ? productPriceLabel
              : '';

          if (enableGiftWithProduct) {
            if (pageType === 'wishlist' && isGiftWithPurchase) {
              dimension166 = dimension166
                ? `${dimension166}, GetFreebies`
                : 'GetFreebies';
            } else if (giftAvailable) {
              dimension166 = dimension166
                ? `${dimension166}, GetFreebies`
                : 'GetFreebies';
            }
          }

          if (enableRushDelivery || enablePriorityDeliveryPhase2) {
            dimension166 = dimension166
                ? `${dimension166}, ${PLP_BUCKET[`${deliverySlaTag}`]}`
                : PLP_BUCKET[`${deliverySlaTag}`];
          }

          acc.push({
            name,
            id: colorGroup ? colorGroup?.split('_')[0] : code?.split('_')[0],
            price: productPrice,
            wasProductPrice,
            brand: brandName,
            category: planningCategory,
            variant: colorGroup ? colorGroup?.split('_')[1] : code?.split('_')[1],
            list: fromJioAds
              ? 'jio ads banner plp screen'
              : fromFloatingWidget
              ? 'video floating widget banner plp'
              : fromVideoBanner
              ? 'video banner plp'
              : fleek
              ? `fleek_${
                  brandPageCode
                    ? `b_${brandName} ${this.props.freeTextSearch}`
                    : `m_${this.props.freeTextSearch}`
                }`
              : isCuratedWidget
              ? `curated_widget_${widgetName}_${optionName}`
              : pageListType,
            position: results.indexOf(id),
            dimension12: code,
            dimension13: name,
            dimension14: '',
            dimension15: planningCategory,
            dimension16: planningCategory,
            dimension20: '',
            dimension21: brandName,
            dimension22: productPrice,
            dimension23: productListType
            ? `${catalogIdToStoreName(catalog || catalogName)}|${productListType}`
            : catalogIdToStoreName(catalog || catalogName),
            dimension24: '',
            dimension36: segmentNameText ? segmentNameText : brickCategory,
            dimension41:
              pageType !== 'wishlist' && toggleView ? (toggleView == 1 ? 'list' : 'grid') : '',
            dimension59: isWishlistPage ? dimension59Data : '',
            dimension60: dimension60Data,
            dimension161: campaignId || '',
            dimension162: campaignId ? 'yes' : 'no',
            dimension163: colorGroup,
            dimension165: productStickerName,
            dimension166,
            dimension167: '',
            dimension170: getDiscountValue(discountPercent),           
            ...(averageRating && {
              dimension46: `${averageRating}|${
                pageType === 'wishlist' ? numUserRatings : ratingCount
              }`
            }),
            adsFlag: adsData?.config?.uclid ? {
              ...adsData,
              config: {
                ...adsData?.config,
                uclid: adsData?.config?.uclid || '',
                sclid: adsData?.config?.sclid || '',
                cid: adsData?.config?.cid || osmosClientId || '',
                ad_source: adsData?.ad_source || '',
                mcid: adsData?.config?.mcid || ''
              }
            } : {},
            code: code,
            sticker_name: productTagEngineValue,
            catalogName: catalogName ?? "",
            sellerId: sellerId ?? "",
          });
        }
        return acc;
      }, []);

      
      const productsForJioAds = ids.reduce((acc, id) => {
        const product = entities[id];
        if (product) {
          acc.push(product);
        }
        return acc;
      }, []);
      const {enablePlaOnPLP = false, deviceType = ''} = this.props;

      products.forEach(product => {
        if (enablePlaOnPLP && Object.keys(product.adsFlag || {}).length > 0) {
          try {
            const event_time = Date.now();
            const impressionTrackerUrl = new URL('https://t.o-s.io/events?event_name=funnel_impression');
            const cli_ubid = signInCheck()
              ? sha256Hex(readCookie('CI')) || ''
              : sha256Hex(readCookie('segment_device_id')) || '';
            const { config: { cid: client_id = '', uclid = '' } = {} } = product.adsFlag || {};

            const paramsToAdd = {
              client_id: client_id || getPlaAdsClientID(window?.__PRELOADED_STATE__) || '',
              cli_ubid,
              uclid: uclid || '',
              event_time: event_time.toString(),
              os_name: deviceType === 'mobile' ? 'm_web' : 'Desktop',
              position: product.position,
            };
            Object.entries(paramsToAdd).forEach(([key, value]) => {
              if (value !== undefined && value !== '') {
                impressionTrackerUrl.searchParams.set(key, value);
              }
            });
            fetch(impressionTrackerUrl.toString(), {
              method: 'GET',
              headers: {
                accept: 'application/json'
              }
            })
              .then(() => {
                console.log('OSMOS product impression tracker (legacy) fired successfully 🚀');
              })
              .catch(err => {
                console.log('OSMOS product impression tracker (legacy) failed', err);
              });
          } catch (error) {
            console.log('Error firing OSMOS product impression tracker (legacy)', error);
          }
        }
      });

      let isPLPSource = fromJioAds ? 'jio ads' : (isCuratedWidget || isDynamicWidget) ? 'widget' : fromPage;

        const urlParams = new URLSearchParams(queryString);
        const sourcePage = isDynamicWidget ? 'widget plp screen' : urlParams.get('referrer');
        if(sourcePage === 'fleek_curated_plp_screen'){
          plp_source_details = urlParams.get('plp_source_details');
          isPLPSource = 'pdp screen  -visit ajiogram store'
        }
      const itemSourceDetail = isCopilotReferrer() ? 'ai_assistant_widget' : 'Product Grid';
      const validToken = getValidAttributionToken()

      const bottomCardFilterValue = this.getSelectedFilterForSegment();

      products.length > 0 &&
        gtmListingProductImpressions(
          products,
          showPlpExtendedBanner,
          plp_source_details,
          isPLPSource,
          this.props.freeTextSearch,
          previousSource,
          sourcePage,
          null,          
          itemSourceDetail,  
          'Vertical',
          validToken,
          null,
          pageCategory,
          bottomCardFilterValue
        );
      fireAddTrackers(productsForJioAds, 'impression');

      const updateStorage = () => {
        const productImpressions = JSON.parse(sessionStorage?.getItem(`${pageUrl}`)) || [];
        const newProductImpressions = [...productImpressions, ...ids];
        sessionStorage?.setItem(`${pageUrl}`, JSON.stringify(newProductImpressions));
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(updateStorage, { timeout: 2000 });
      } else {
        setTimeout(updateStorage, 100);
      }
    }
  };

  /**
   * ⚡ CODE SPLITTING: Use split module for scroll impression tracking
   * Falls back to legacy implementation if modules not loaded
   */
  trackImpressions = (
    rowHeightOfGrid,
    results,
    productImpressionData,
    entities,
    sendImpressionData
  ) => {
    if (this.splitModules?.createTrackImpressions) {
      return this.splitModules.createTrackImpressions(
        rowHeightOfGrid,
        results,
        productImpressionData,
        entities,
        sendImpressionData,
        this.props,
        this.scrollTotalCount
      );
    }

    // 🔄 LEGACY: Fallback to original implementation
    return (scrollEventData = {}) => {
      const { deviceType, changeView, gridResults } = this.props;
      const { scrollTop = 0 } = scrollEventData;
      const { products = [] } = productImpressionData;
      const scrollValueForListView = 1;
      let scrollValue = 2;
      if (deviceType === 'desktop') {
        scrollValue = 3;
      } else if (changeView) {
        scrollValue = scrollValueForListView;
      }

      let scrollCount =
        Math.floor(scrollTop / rowHeightOfGrid) * scrollValue +
        (changeView ? scrollValueForListView : 4);
      if (scrollValue === 2) {
        const productsRow = Math.ceil(scrollCount / 2);
        let adCount = 0;
        for (let i = 0; i < productsRow; i++) {
          if (gridResults[i]) {
            const [p1, p2] = gridResults[i];
            if (p1 && p2) {
              if (p1.includes('ad') || p2.includes('ad')) {
                adCount++;
              }
            }
          }
        }
        scrollCount -= adCount;
      } else if (scrollValue === 1) {
        const { jioAdStartPositionMSite, jioAdsFrequencyMsite } = this.props;
        if (scrollCount > jioAdStartPositionMSite) {
          const adsShown =
            Math.round((scrollCount - jioAdStartPositionMSite) / (2 * jioAdsFrequencyMsite)) + 1;
          scrollCount -= adsShown;
        }
      }

      this.scrollTotalCount(scrollCount);

      if (products.length >= 10) {
        const chunks = chunk(products, 10);
        this.productImpressionData.products = chunks.reduce((acc, chunkArr) => {
          if (chunkArr.length === 10) {
            sendImpressionData(chunkArr, entities, results);
            return acc;
          }
          return Array.isArray(chunkArr) ? chunkArr : [];
        }, []);
      }
    };
  };

  scrollTotalCount = scrollCount => {
    const wrappedElement = this.gridContainer;
    const { updateProductScrollCount = () => {}, results = [] } = this.props;
    if (wrappedElement.getBoundingClientRect().bottom == window.innerHeight) {
      updateProductScrollCount(results.length);
    } else {
      updateProductScrollCount(scrollCount);
    }
  };

  triggerOnClickProduct = () => {
    const { deviceType } = this.props;
    if (deviceType === 'mobile') {
      this.setState({ productClicked: true });
    }
    this.sendRemainingImpressions();
    if (deviceType === 'desktop') {
      this.productImpressionData.products = [];
    }
  };

  triggerIfProductIsOnView = ({ productId }) => {
    if (this.productImpressionData.products.indexOf(productId) === -1) {
      this.productImpressionData.products.push(productId);
    }
  };

  /**
   * ⚡ CODE SPLITTING: Use split module for JioAds banner logic
   * Falls back to legacy implementation if modules not loaded
   */
  bannerJioAds = (rowIndex, columnIndex, style, key, jioAdsABFVarientFlag, displayRatingWidget) => {
    // Use split module if available and feature flag is enabled
    if (this.splitModules?.getBannerJioAds) {
      return this.splitModules.getBannerJioAds(
        rowIndex,
        columnIndex,
        style,
        key,
        jioAdsABFVarientFlag,
        displayRatingWidget,
        this.props,
        this.adbannersInViewPort
      );
    }
    // Note: Full legacy implementation preserved in original code
    // This is a simplified fallback - full implementation should be kept for safety
    const {
      changeView,
      jioAdsFrequencyMsite,
      jioAdsRowCount,
      jioAdsStartRowDesktop,
      jioAdStartPositionMSite,
      listingAds,
      jioAdsEndPositionMsite,
      jioAdsEndRowDesktop,
      gridColumns = 3,
      gridResults,
      plpUnratedProdWidgetPosition,
      ratingConflictWithBanner,
      freeTextSearch,
      deviceType,
      showAdTag,
      currentStore,
      isPlpBannerOsmosEnabled = false
    } = this.props;
    let adType = null;
    let jioAd = null;
    const styleModified = { ...style };
    const gaAddPositionMsite = rowIndex + 1 + (columnIndex + 1);
    const gaAddPositionWeb = rowIndex + 1;
    const page = window.location.href.includes('/wishlist');
    if (jioAdsABFVarientFlag === '0') {
      return { adType, styleModified, jioAd };
    }

    const id = get(gridResults, [rowIndex, columnIndex]);

    if (typeof id === 'string' && id.includes('ad')) {
      adType = 'grid';
      let { top } = style;
      if (
        displayRatingWidget &&
        !ratingConflictWithBanner &&
        rowIndex === plpUnratedProdWidgetPosition + 1
      ) {
        top -= style.height - 277;
      }
      if (
        rowIndex > plpUnratedProdWidgetPosition + 2 &&
        displayRatingWidget &&
        style &&
        style.top &&
        style.height
      ) {
        top = top - 97;
      }
      const adIndex = parseInt(id.split('-')[1]);
      const gaAddPositionMsite = rowIndex + 1 + (columnIndex + 1);
      const gaAddPositionWeb = rowIndex + 1;

      jioAd = (
        <JioAds
          showAdTag={showAdTag}
          inViewPort={this.adbannersInViewPort}
          listingAds={listingAds}
          dimension={bannerAdViewType.grid}
          adPos={adIndex + 1}
          data={listingAds[adIndex]}
          style={{ ...style, top }}
          ratingWidgetPosition={!ratingConflictWithBanner ? plpUnratedProdWidgetPosition : null}
          freeTextSearch={freeTextSearch}
          gaAddPosition={deviceType === 'mobile' ? gaAddPositionMsite : gaAddPositionWeb}
          currentStore={currentStore}
          isPlpBannerOsmosEnabled={isPlpBannerOsmosEnabled}
        />
      );
      return { adType, styleModified, jioAd };
    }

    let adIndex = null;
    let dimension = null;
    const adCount = listingAds.length;
    // Note: Full legacy implementation should be preserved here for complete fallback
    if (gridColumns === 1 || changeView) {
      adType = 'list';
      dimension = bannerAdViewType.list;
      if (rowIndex >= jioAdStartPositionMSite) {
        // let diff = jioAdStartPositionMSite > jioAdsFrequencyMsite ? jioAdStartPositionMSite - jioAdsFrequencyMsite : 0;
        // as we don't want to go index beyound the jioAdsEndPositionMsite
        let idx = Math.min(rowIndex, jioAdsEndPositionMsite);
        let currVal = Math.floor((idx - jioAdStartPositionMSite) / jioAdsFrequencyMsite) + 1;
        adIndex = currVal >= adCount ? adCount : currVal;
        styleModified.top += 150 * adIndex;
        if ((idx - jioAdStartPositionMSite) % jioAdsFrequencyMsite === 0) {
          jioAd = (
            <JioAds
              showAdTag={showAdTag}
              inViewPort={this.adbannersInViewPort}
              listingAds={listingAds}
              dimension={dimension}
              adPos={adIndex}
              key={key}
              data={listingAds[adIndex - 1]}
              style={{ ...style, top: styleModified.top - 125, width: '100%', height: 100 }}
              freeTextSearch={freeTextSearch}
              gaAddPosition={deviceType === 'mobile' ? gaAddPositionMsite : gaAddPositionWeb}
              currentStore={currentStore}
              // eslint-disable-next-line prettier/prettier
              // eslint-disable-next-line react/jsx-closing-tag-location
            ></JioAds>
          );
        }
        if (currVal > adCount || rowIndex > jioAdsEndPositionMsite || columnIndex !== 0) {
          jioAd = null;
        }
      }
      return { adType, styleModified, jioAd };
    }
    if (gridColumns === 2 && jioAdsABFVarientFlag === '2') {
      adType = 'list';
      dimension = bannerAdViewType.list;
      const startRow = Math.ceil(jioAdStartPositionMSite / 2);
      const freq = Math.ceil(jioAdsFrequencyMsite / 2);
      if (rowIndex >= startRow) {
        // let diff = jioAdStartPositionMSite > jioAdsFrequencyMsite ? jioAdStartPositionMSite - jioAdsFrequencyMsite : 0;
        // as we don't want to go index beyound the jioAdsEndPositionMsite
        let idx = Math.min(rowIndex, jioAdsEndPositionMsite);
        const currVal = Math.floor((idx - startRow) / freq) + 1;
        adIndex = currVal >= adCount ? adCount : currVal;
        styleModified.top += displayRatingWidget && !page ? 120 * adIndex : 150 * adIndex;
        if ((idx - startRow) % freq === 0) {
          // let newTop = (displayRatingWidget) ? rowIndex > plpUnratedProdWidgetPosition + 1 ? 275 : rowIndex === plpUnratedProdWidgetPosition +1  ? 300 : 125 : adIndex === 1 ? 145 : 165;
          let newTop;

          if (displayRatingWidget) {
            if (rowIndex > plpUnratedProdWidgetPosition + 1 && !page) {
              newTop = 305;
            } else if (rowIndex === plpUnratedProdWidgetPosition + 1 && !page) {
              newTop = 275;
            } else {
              newTop = 125;
            }
          } else {
            newTop = adIndex === 1 ? 145 : 165;
          }

          jioAd = (
            <JioAds
              showAdTag={showAdTag}
              inViewPort={this.adbannersInViewPort}
              listingAds={listingAds}
              dimension={dimension}
              adPos={adIndex}
              key={key}
              data={listingAds[adIndex - 1]}
              style={{ ...style, top: styleModified.top - newTop, width: '100%', height: 100 }} // Here, we are repositioning the jio ads
              freeTextSearch={freeTextSearch}
              gaAddPosition={deviceType === 'mobile' ? gaAddPositionMsite : gaAddPositionWeb}
              currentStore={currentStore}
              // eslint-disable-next-line react/jsx-closing-tag-location
            ></JioAds>
          );
        }
        if (currVal > adCount || rowIndex > jioAdsEndPositionMsite || columnIndex !== 0) {
          jioAd = null;
        }
      }
      return { adType, styleModified, jioAd };
    }

    if (gridColumns >= 3) {
      adType = 'desktop';
      dimension = bannerAdViewType.desktop;

      if (rowIndex >= jioAdsStartRowDesktop) {
        // this is for allocating spaces for ads in the upcoming rows
        // rowIndex-jioAdsStartRowDesktop)/jioAdsRowCount)+1
        const idx = Math.min(rowIndex, jioAdsEndRowDesktop);
        let currVal = Math.floor((idx - jioAdsStartRowDesktop) / jioAdsRowCount) + 1;
        adIndex = currVal >= adCount ? adCount : currVal;
        styleModified.top += 150 * adIndex;
        // this condition is needed as for the row which satisfies this condtion will show
        if ((idx - jioAdsStartRowDesktop) % jioAdsRowCount === 0) {
          // let newTop = displayRatingWidget ? rowIndex > plpUnratedProdWidgetPosition + 1 ? 375 : rowIndex === plpUnratedProdWidgetPosition +1  ? 380 : 125 : 125;
          let newTop;

          if (displayRatingWidget) {
            if (rowIndex > plpUnratedProdWidgetPosition + 1) {
              newTop = 375;
            } else if (rowIndex === plpUnratedProdWidgetPosition + 1) {
              newTop = 380;
            } else {
              newTop = 125;
            }
          } else {
            newTop = 125;
          }
          jioAd = (
            <JioAds
              showAdTag={showAdTag}
              inViewPort={this.adbannersInViewPort}
              listingAds={listingAds}
              dimension={dimension}
              adPos={adIndex}
              data={listingAds[adIndex - 1]}
              style={{ ...style, top: styleModified.top - newTop, width: '100%', height: 100 }}
              freeTextSearch={freeTextSearch}
              gaAddPosition={deviceType === 'mobile' ? gaAddPositionMsite : gaAddPositionWeb}
              currentStore={currentStore}
            ></JioAds>
          );
        }
        if (currVal > adCount || rowIndex > jioAdsEndRowDesktop || columnIndex !== 0) {
          jioAd = null;
        }
      }

      return { adType, styleModified, jioAd };
      // adding the condition for desktop, as gridColumns is more than 3 for desktop
    }
    return { adType, styleModified, jioAd };
  };



  cellRenderer = ({ columnIndex, key, rowIndex, style }) => {
    const {
      gridResults = [],
      results,
      deviceType = '',
      onDisplayRemoveItemDialog,
      onDisplayHideDialog,
      onRemoveItem,
      newDesignLuxe,
      pageType,
      msiteLuxe,
      isOldView,
      gridColumns = 3,
      isEarlyAccessUser,
      changeView,
      jioAdsFrequencyMsite,
      jioAdsRowCount,
      jioAdsStartRowDesktop,
      jioAdStartPositionMSite,
      listingAds,
      jioAdsEndPositionMsite,
      jioAdsEndRowDesktop,
      jioAdsABFVarientFlag,
      plpUnratedProdWidgetPosition,
      wishlistUnratedProdWidgetPosition,
      unratedItems = {},
      enableRating,
      ratingConflictWithBanner,
      freeTextSearch,
      widgets,
      enablePlpLcpV2,
      enablePlpInpV1,
      enablePlpLcpOptimizations,
      replaceInHistory,
      fetchNextFacets,
      updateFilterModal
    } = this.props;
    let id = get(gridResults, [rowIndex, columnIndex]);
    if (!id) {
      return null;
    }
    const plpRatingWidget = getFeatureVariant('plpRatingWidget');
    const wishlistRatingWidget = getFeatureVariant('wishlistRatingWidget');
    const { userUnratedItems = [] } = unratedItems;
    const displayRatingWidget =
      enableRating &&
      // ((wishlistRatingWidget === '1' && pageType === 'wishlist') ||
      //   (plpRatingWidget === '1' && pageType === 'plp')) &&
      (!this.state.unratedProductsAvailable || userUnratedItems.length > 0) &&
      !changeView &&
      deviceType === 'mobile' &&
      !ratingConflictWithBanner;


// yah pe jio ads ko nhi pata ki widget has already been rendered. 
// so we need to tell j
    let { adType, jioAd, styleModified } = this.bannerJioAds(
      rowIndex,
      columnIndex,
      style,
      key,
      jioAdsABFVarientFlag,
      displayRatingWidget
    );


    if (gridResults[rowIndex][columnIndex].includes('WIDGET')) {
      const widgetIndex = parseInt(gridResults[rowIndex][columnIndex].split('_')[1]);
      if (columnIndex === 0) {
        return (
            <WidgetUI widget={widgets[widgetIndex]} style={style} />
        );
      } else {
        return null;
      }
    }

    // Handle INLINE_FILTERS rows
    if (gridResults[rowIndex][columnIndex].includes('INLINE_FILTERS')) {
      const filterIndex = parseInt(gridResults[rowIndex][columnIndex].split('_')[2]) || 0;
      if (columnIndex === 0) {
        // Adjust position if RatingWidget is displayed and this row is after it
        let adjustedStyle = { ...style, width: '100vw' };
        if (
          displayRatingWidget &&
          !ratingConflictWithBanner &&
          rowIndex > plpUnratedProdWidgetPosition + 2 &&
          style &&
          style.top
        ) {
          // Adjust top position to account for RatingWidget extra height
          adjustedStyle = { ...adjustedStyle, top: style.top - 97 };
        }
        return (
          <div key={key} style={adjustedStyle}>
            <InlineFilters filterIndex={filterIndex} replaceInHistory={replaceInHistory} fetchNextFacets={fetchNextFacets} updateFilterModal={updateFilterModal} />
          </div>
        );
      }
      return null;
    }

    const styleWidgetModif = {...styleModified}


    const isWishListPage = window.location.href.includes('/wishlist');


    if (adType === 'grid' && !isWishListPage) {
      return <div key={key}>{jioAd}</div>;
    }

    const measureSize = rowIndex === 0 && columnIndex === 0;
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const jioAdsSource = urlParams.get('jioAdsSource');
    const sourcePage = urlParams.get('sourcePage');

    const isAboveFold = enablePlpLcpV2 && rowIndex < 2;
    const productPosition = results.indexOf(id);

    return (
      <div key={key}>
        {jioAd}
        {<ProductCard
              newDesignLuxe={newDesignLuxe}
              msiteLuxe={msiteLuxe}
              wishlistOptionCodeUpdates={this.state.wishlistOptionCodeUpdates}
              measureSize={measureSize}
              sizeInfo={{ heightOffset: 15 }}
              id={id}
              position={productPosition}
              style={styleModified}
              pageTitle={this.props.pageTitle}
              deviceType={deviceType}
              onDisplayRemoveItemDialog={onDisplayRemoveItemDialog}
              onDisplayHideDialog={onDisplayHideDialog}
              onRemoveItem={onRemoveItem}
              pageType={pageType}
              onClickProduct={this.triggerOnClickProduct}
              username={this.props.username}
              isOldView={isOldView}
              offerPriceShow={false} // this is based on the optimise experiement, I have removed that code, as it is not in use
              gridColumns={gridColumns}
              isEarlyAccessUser={isEarlyAccessUser}
              triggerIfProductIsOnView={this.triggerIfProductIsOnView}
              pageUrl={this.props.pageUrl}
              similarApiCall={this.props.similarApiCall}
              similarProductViewed={this.props.similarProductViewed}
              changeView={this.props.changeView}
              toggleView={this.props.toggleView}
              plpUnratedProdWidgetPosition={plpUnratedProdWidgetPosition}
              wishlistUnratedProdWidgetPosition={wishlistUnratedProdWidgetPosition}
              rowIndex={rowIndex}
              columnIndex={columnIndex}
              displayRatingWidget={displayRatingWidget}
              displayWidgetShimmer={!this.state.unratedProductsAvailable}
              unratedItems={userUnratedItems}
              sourcePage={sourcePage}
              jioAdsSource={jioAdsSource}
              listingAds={listingAds?.length > 0}
              isJioAd={
                jioAdsABFVarientFlag === '2' && displayRatingWidget && listingAds?.length > 0
              }
              isAboveFold={isAboveFold}
              enablePlpInpV1={enablePlpInpV1}
            />}
      </div>
    );
  };

  renderGridInMobile() {
    const {
      gridRowCount,
      gridColumnWidth,
      gridRowHeight,
      gridColumns,
      gridWidth = 320,
      request,
      gridResults = [],
      results = [],
      msiteLuxe,
      entities,
      pageType,
      flags,
      priceRevealMetaInfo = {},
      deviceType = '',
      currentStore,
      changeView,
      enableRating,
      enableRatingLabelPLP,
      enableRatingLabelWishlist,
      enableGiftWithProduct,
      enablePriorityDeliveryPhase2,
      enableRushDelivery,
    } = this.props;
    const page = window.location.href.includes('/wishlist');
    const wishlistCard =
      enableGiftWithProduct && msiteLuxe ? 412 - gridRowHeight : 391 - gridRowHeight;
    const screenWidth = window.innerWidth;
    let isPLPPriceReveal = false;

    function cardHeight({ index }) {
      const isRushEnabled = enableRushDelivery || enablePriorityDeliveryPhase2;
      // console.log("CardHeight triggered", index)
      if(gridResults[index][0].includes('WIDGET')){
        return 400
      }
      // Height for InlineFilters row - adjust based on content
      if (gridResults[index][0].includes('INLINE_FILTERS')) {
        return 250; // Base height for InlineFilters
      }
      let cardHeight =
      page && screenWidth < 380
        ? gridRowHeight + (wishlistCard > 0 ? wishlistCard : 0)
        : gridRowHeight;
    cardHeight = changeView ? (msiteLuxe ? 381 : 431) : cardHeight;
      let plpCardHeight;
      if (msiteLuxe) plpCardHeight = isPLPPriceReveal ? cardHeight - 30 : cardHeight;
      else plpCardHeight = isPLPPriceReveal ? cardHeight - 40 : cardHeight - 50;
      let wishlistHeight = isPLPPriceReveal ? cardHeight - 30 : cardHeight;
      cardHeight = pageType !== 'wishlist' ? plpCardHeight : wishlistHeight;
      cardHeight = changeView ? isRushEnabled ? cardHeight + 40 : cardHeight + 20 : cardHeight;
      return  cardHeight;
    }

      // let cardHeight = page && screenWidth < 380 ? gridRowHeight + wishlistCard : gridRowHeight;
      // cardHeight = changeView ? (msiteLuxe ? 381 : 431) : cardHeight;
   
    if ((pageType == 'plp' || pageType == 'pdp') && Object.keys(entities).length) {
      const codes = Object.keys(entities);
      const entityFirst = entities[codes[0]];

      const priceRevealState = getPriceRevealSaleDuration(
        flags,
        deviceType,
        priceRevealMetaInfo,
        currentStore
      );
      isPLPPriceReveal = !!(!entityFirst.isDODEnabled && priceRevealState);
    }
    const plpRatingLabel = getVariant('rating-label-display-msite-plp') || '0';
    const wishlistRatingLabel = getVariant('rating-label-display-msite-wishlist') || '0';

    // let plpCardHeight;
    // if (msiteLuxe) plpCardHeight = isPLPPriceReveal ? cardHeight - 30 : cardHeight;
    // else plpCardHeight = isPLPPriceReveal ? cardHeight - 40 : cardHeight - 50;
    // let wishlistHeight = isPLPPriceReveal ? cardHeight - 30 : cardHeight;
    // cardHeight = pageType !== 'wishlist' ? plpCardHeight : wishlistHeight;
    // cardHeight = changeView ? cardHeight + 20 : cardHeight;
    return (
      <>
        <WindowScroller
          request={request}
          gridResults={gridResults}
          results={results}
          onScroll={
            msiteLuxe || pageType === 'pdp'
              ? this.luxeTrackImpressions(
                  gridRowHeight,
                  results,
                  this.productImpressionData,
                  this.props.entities,
                  this.luxeSendProductImpressionData
                )
              : this.trackImpressions(
                  gridRowHeight,
                  results,
                  this.productImpressionData,
                  this.props.entities,
                  this.sendImpressionData
                  
                )
          }
        >
          {({ height, isScrolling, onChildScroll, scrollTop }) => {
            return (
              <ColumnSizer
                columnMaxWidth={900}
                columnMinWidth={changeView ? 300 : gridColumnWidth}
                columnCount={gridColumns}
                key="GridColumnSizer"
                width={gridWidth}
                request={request}
                gridResults={gridResults}
              >
                {({ adjustedWidth, columnWidth, registerChild }) => (
                  <Grid
                    aria-readonly={false}
                    role="grid"
                    aria-label='Products List'
                    key={`grid-${gridRowHeight}`}
                    ref={node => {
                      registerChild(node);
                      this.gridRef.current = node;
                    }}
                    cellRenderer={this.cellRenderer}
                    columnCount={changeView ? 1 : gridColumns}
                    columnWidth={changeView ? adjustedWidth : columnWidth}
                    height={height}
                    rowCount={gridRowCount}
                    rowHeight={cardHeight}
                    isScrolling={isScrolling}
                    scrollTop={scrollTop}
                    autoHeight
                    autoContainerWidth
                    width={changeView ? window.innerWidth : adjustedWidth}
                    className={`items rilrtl-products-list rilrtl-products-list--gridColumns-${gridColumns}`}
                    request={request}
                    gridResults={gridResults}
                    overscanRowCount={this.props.enablePlpLcpV2 ? 4 : Math.max(20, gridResults.length)}
                    overscanIndicesGetter={({
                      cellCount,
                      overscanCellsCount,
                      startIndex,
                      stopIndex
                    }) => {
                      return {
                        overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
                        overscanStopIndex: Math.min(cellCount - 1, stopIndex + overscanCellsCount)
                      };
                    }}
                  />
                )}
              </ColumnSizer>
            );
          }}
        </WindowScroller>
      </>
    );
  }

  renderGrid() {
    let {
      gridRowCount,
      gridColumnWidth,
      gridRowHeight,
      gridColumns = 3,
      gridWidth = 320,
      request = {},
      gridResults = [],
      metaData: { sis = false, name = '' } = {},
      dimension = {},
      newDesignLuxe,
      results,
      // capsuleTotalWidth = '',
      entities,
      flags,
      priceRevealMetaInfo = {},
      deviceType = '',
      currentStore,
      pageType,
      enableRatingLabelPLP,
      enableRatingLabelWishlist,
      jioAdsRowCount,
      jioAdsStartRowDesktop,
      jioAdsEndRowDesktop,
      listingAds,
      enableGiftWithProduct
    } = this.props;

    let { gridWrapperWidth: width = 1000 } = request.query;
    let isLuxeWishlistPriceReveal = false;
    let columnWidth = 323;
    let rowHeight = enableGiftWithProduct ? newDesignLuxe ? 556 : 580 : 535;
    if (gridColumns == 5) {
      columnWidth = 194;
      rowHeight = enableGiftWithProduct ? newDesignLuxe ? 385 : 365 : 345;
    }

    if (gridColumns == 4) {
      columnWidth = 305;
      rowHeight = enableGiftWithProduct ? newDesignLuxe ? 501 : 490 : 480;
    }

    if (
      ((getFeatureVariant('rating-label-display-msite-wishlist') !== '0' &&
        pageType === 'wishlist' &&
        enableRatingLabelWishlist) ||
        (getFeatureVariant('rating-label-display-msite-plp') !== '0' &&
          pageType !== 'wishlist' &&
          enableRatingLabelPLP)) &&
      (gridColumns === 4 || gridColumns === 5)
    ) {
      rowHeight += 40;
    }


    // 0.6363636364
    // if(capsuleTotalWidth == 1200){
    //   width = 1200;
    // }

    if (
      newDesignLuxe &&
      pageType === 'wishlist' &&
      deviceType === 'desktop' &&
      Object.keys(entities).length
    ) {
      const codes = Object.keys(entities);
      const entityFirst = entities[codes[0]];

      const priceRevealState = getPriceRevealSaleDuration(
        flags,
        deviceType,
        priceRevealMetaInfo,
        currentStore
      );
      isLuxeWishlistPriceReveal = !!(!entityFirst.isDODEnabled && priceRevealState);
    }

    /*
    Here as we are adding banner ads we will need to increase the row count
    As banner ads take the one whole row of height 150px
    for a 5 col grid Row height is 350px
    for a 3  col grid Row height is 480px
    So we need ad count - ad count depend on
      - total products
      - total ads
      - ads end row position
    After getting the adCount we can calulcate  
    rowAdjustmentFactor = Math.ceil((adCount*150/rowHeight))

    Calculating gridRowCount
    Min of (  (min(lastrow, endRow)- startRow)/rowFreq and adCount) 

    endRow = 35
    startRow = 2
    rowFreq = 2

    */
    let rowAdjustmentFactor = 0;
    // As jioads are not being used in luxe so no need for adjusting factor
    if (listingAds.length > 0 && !newDesignLuxe) {
      const adCount = Math.min(
        Math.floor(
          (Math.min(gridRowCount, jioAdsEndRowDesktop) - jioAdsStartRowDesktop) / jioAdsRowCount
        ) + 1,
        listingAds.length
      );
      rowAdjustmentFactor = Math.round((adCount * 160) / rowHeight);
    }
    return (
      <WindowScroller
        onScroll={
          newDesignLuxe || pageType === 'pdp'
            ? this.luxeTrackDesktopImpressions(
                gridRowHeight,
                results,
                this.productImpressionData,
                this.props.entities,
                this.luxeSendProductImpressionData
              )
            : this.trackImpressions(
                gridRowHeight,
                results,
                this.productImpressionData,
                this.props.entities,
                this.sendImpressionData
              )
        }
      >
        {({ height, isScrolling, onChildScroll, scrollTop }) => {
          return (
            <Grid
              role="presentation"
              ref={node => {
                this.gridRef.current = node;
              }}
              cellRenderer={this.cellRenderer}
              columnCount={gridColumns}
              columnWidth={columnWidth}
              height={height}
              rowCount={
                gridRowCount +
                rowAdjustmentFactor
              }
              rowHeight={isLuxeWishlistPriceReveal ? rowHeight + 15 : rowHeight}
              isScrolling={isScrolling}
              scrollTop={scrollTop}
              autoHeight
              autoContainerWidth
              width={width}
              className={newDesignLuxe ? `items column-${gridColumns}` : 'items'}
              overscanRowCount={this.props.enablePlpLcpV2 ? 4 : Math.max(20, gridResults.length)}
              overscanIndicesGetter={({ cellCount, overscanCellsCount, startIndex, stopIndex }) => {
                return {
                  overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
                  overscanStopIndex: Math.min(cellCount - 1, stopIndex + overscanCellsCount)
                };
              }}
            />
          );
        }}
      </WindowScroller>
    );
  }

  renderGridBasedOnDevice() {
    const { deviceType = '' } = this.props;
    if (deviceType === 'mobile') {
      return this.renderGridInMobile();
    } else {
      return this.renderGrid();
    }
  }

  render() {
    const newDesignLuxe = this.props.newDesignLuxe;
    const {
      gridResults = [],
      showLoadMoreButton = true,
      showProductsLoader = false,
      request = {},
      entities = {},
      loadingMoreItems = false,
      isEarlyAccessUser,
      changeView,
      freeTextSearch
    } = this.props;
  
    
    let { totalResults = 0 } = request;

    if (totalResults == 0) {
      return null;
    }
    return (
      <div
        id="main-content"
        className="items"
        ref={gridContainer => {
          this.gridContainer = gridContainer;
        }}
      >
        {gridResults.length > 0 ? this.renderGridBasedOnDevice() : null}
        <div className="load">
          {showProductsLoader ? <Loader name="products-loader" /> : null}
          {/* Sentinel element at the bottom of the grid */}
          <div ref={this.sentinelRef} style={{ height: '1px' }} />
        </div>
      </div>
    );
  }

  sendRemainingImpressions = () => {
    const {
      entities = {},
      results = [],
      msiteLuxe,
      newDesignLuxe,
      deviceType,
      pageType
    } = this.props;
    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);
    const jioBannerSource = urlParams.get('jioAdsSource');
    const sourcePage = urlParams.get('sourcePage');
    const plp_source_details = jioBannerSource
      ? jioBannerSource.replaceAll('[ccb]', Date.now())
      : false;
    const { pageType: fromPage = '' } = getPageInfo(location.href);
    const isPLPSource = sourcePage || false;
    if (msiteLuxe || newDesignLuxe || pageType === 'pdp') {
      deviceType === 'mobile'
        ? this.luxeSendProductImpressionData(this.productImpressionData.products, entities, results)
        : this.luxeProductImpressions(this.productImpressionData.products, entities, results);
    } else {
      this.sendImpressionData(this.productImpressionData.products, entities, results);
    }
  };

  triggerRemainingImpressionsOnSortOrFilter = () => {
    this.sendRemainingImpressions();
    this.productImpressionData.products = [];
  };

  beforeUnloadHandler = () => {
    // console.log('before unload handleer called');
    // alert('Hi before Unload handler')
    // we need to fire GA event for all the jio ads that are shown to the user
    // const bannerInViewPort = localStorage.getItem('bannerInViewPort');

    if (this.adbannersInViewPort.length > 0) {
      gtmPromotionImpressions(this.adbannersInViewPort);
      console.log('Ads Impression event fired', this.adbannersInViewPort);
      this.adbannersInViewPort = [];
    }

    const isProductClicked = this.state.productClicked;
    if (!isProductClicked) {
      this.sendRemainingImpressions();
    }
  };

  fetchUnratedItems = async () => {
    try {
      const response = await this.props.fetchUnratedRatingProducts();
      this.setState({ unratedProductsAvailable: true }, () => {
        // Force grid to recalculate row heights after RatingWidget API responds
        this.recomputeGridSize();
      });
    } catch {
      this.setState({ unratedProductsAvailable: true }, () => {
        this.recomputeGridSize();
      });
    }
  };

  // Recalculate grid row heights when content changes dynamically
  recomputeGridSize = () => {
    if (this.gridRef.current && typeof this.gridRef.current.recomputeGridSize === 'function') {
      this.gridRef.current.recomputeGridSize();
    }
  };

  componentDidMount() {
    this.componentDidMountOptimized();
  }

  /**
   * ⚡ OPTIMIZED VERSION - componentDidMount (Sprint 26_01)
   * TBT Reduction: ~400ms
   */
  componentDidMountOptimized() {
    const { isPDP, plpLoadMargin } = this.props;
    const { width = 320 } = elementSize(this.gridContainer) || {};

    // ⚡ CRITICAL: Set up IntersectionObserver (needed for infinite scroll)
    this.setupIntersectionObserver(plpLoadMargin);

    // ⚡ CRITICAL: Set grid config (needed for rendering)
    /* eslint-disable react/destructuring-assignment */
    if (isPDP) {
      this.props.setGridConfigPDP({ gridWidth: width });
    } else {
      this.props.setGridConfig({ gridWidth: width });
    }
    /* eslint-enable react/destructuring-assignment */

    // Align SSR cards exactly with React grid position, then hide
    const ssrOuter = document.getElementById('plp-ssr-cards');
    const ssrInner = document.getElementById('plp-ssr-cards-inner');
    const cardGrid = document.querySelector('.card-padding-top');
    if (ssrOuter && ssrInner && cardGrid) {
      const diff = cardGrid.getBoundingClientRect().top - ssrOuter.getBoundingClientRect().top;
      ssrInner.style.transform = `translateY(${diff}px)`;
    }

    if (typeof window.__hideSSRCards === 'function') {
      // Option 1: wait for first 2 CSR card images to decode before hiding SSR cards,
      // so the user never sees a blank/loading flash between SSR and CSR.
      // Option 2 (fade): handled inside window.__hideSSRCards.
      let hideTriggered = false;
      const triggerHide = () => {
        if (hideTriggered) return;
        hideTriggered = true;
        requestAnimationFrame(() => window.__hideSSRCards());
      };

      const imgs = cardGrid ? Array.from(cardGrid.querySelectorAll('img')).slice(0, 2) : [];
      if (imgs.length > 0) {
        const decodePromises = imgs.map(img => {
          if (img.complete) return Promise.resolve();
          if (typeof img.decode === 'function') return img.decode().catch(() => {});
          return new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
        });
        Promise.all(decodePromises).then(triggerHide);
        setTimeout(triggerHide, 1500); // fallback: don't block more than 1.5s
      } else {
        triggerHide();
      }
    }

    // ⚡ DEFER: All non-critical work
    this.deferNonCriticalWork();
  }

  /**
   * ⚡ PERFORMANCE OPTIMIZATION
   * Set up IntersectionObserver for infinite scroll
   * Extracted for reuse in optimized version
   */
  setupIntersectionObserver(plpLoadMargin) {
    const observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: plpLoadMargin
    });

    if (this.sentinelRef.current) {
      observer.observe(this.sentinelRef.current);
    }

    this.observer = observer;
  }

  /**
   * ⚡ PERFORMANCE: Pre-bound intersection observer callback (Sprint 26_01)
   * Avoids creating new function on every intersection
   * TBT Reduction: ~120ms
   */
  handleIntersection(entries) {
    const { showLoadMoreButton = true } = this.props;
    const { isFetching } = this.state;

    if (entries[0].isIntersecting && showLoadMoreButton && !isFetching) {
      this.setState({ isFetching: true }, this.loadMoreBound);
    }
  }

  /**
   * ⚡ PERFORMANCE: Pre-bound load more callback
   */
  loadMoreBound() {
    const { loadMoreItems } = this.props;
    loadMoreItems(false, () => this.setState({ isFetching: false }, this.recheckSentinel));
  }

  /**
   * After a load-more completes, re-check if the sentinel is still visible.
   * Handles the race where IntersectionObserver fired while isFetching was true
   * and won't fire again because the sentinel never left the viewport.
   */
  recheckSentinel() {
    const sentinel = this.sentinelRef.current;
    if (!sentinel) return;

    const { showLoadMoreButton = true } = this.props;
    if (!showLoadMoreButton) return;

    const rect = sentinel.getBoundingClientRect();
    const margin = 400;
    if (rect.top <= window.innerHeight + margin) {
      this.setState({ isFetching: true }, this.loadMoreBound);
    }
  }

  /**
   * ⚡ PERFORMANCE OPTIMIZATION
   * Defer non-critical work to avoid blocking main thread
   * This reduces TBT by ~400ms
   */
  deferNonCriticalWork() {
    const { msiteLuxe, newDesignLuxe, deviceType, pageUrl } = this.props;

    // ⚡ Defer event listeners (make them passive for better scroll)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          this.setupEventListenersOptimized();
        },
        { timeout: 1000 }
      );
    } else {
      setTimeout(() => {
        this.setupEventListenersOptimized();
      }, 100);
    }

    // ⚡ Defer session storage cleanup
    if (window.performance && performance.navigation.type === performance.navigation.TYPE_RELOAD) {
      setTimeout(() => {
        if (sessionStorage) {
          sessionStorage.removeItem(`${pageUrl}`);
        }
      }, 200);
    }

    // ⚡ Defer product impression setup
    if (msiteLuxe || newDesignLuxe) {
      const isMobile = deviceType === 'mobile';
      setTimeout(() => {
        if (isMobile) {
          this.luxeSetDefaultProductImpression();
        } else {
          this.luxeSetDefaultDesktopProductImpression();
        }
      }, 300);
    }

    // ⚡ Defer low priority tasks
    setTimeout(() => {
      this.hitWishlistOptionCodesAPI();
      this.setGridScroll();
      this.fetchUnratedItems();
    }, 500);
  }

  /**
   * ⚡ PERFORMANCE OPTIMIZATION
   * Set up event listeners with passive option for better scroll performance
   */
  setupEventListenersOptimized() {
    const pageshowHandler = event => this.checkPageLoadedViaCache(event, 'onPageShow');
    const popstateHandler = event => this.checkPageLoadedViaCache(event, 'isPOPState');

    // ⚡ Add passive: true to prevent scroll blocking
    window.addEventListener('pageshow', pageshowHandler, { passive: true });
    window.addEventListener('popstate', popstateHandler, { passive: true });
    window.addEventListener('beforeunload', this.beforeUnloadHandler, { passive: true });

    // Store references for cleanup
    this.pageshowHandler = pageshowHandler;
    this.popstateHandler = popstateHandler;
  }

  componentDidUpdate(prevProps) {
    const { msiteLuxe, newDesignLuxe, pageType, deviceType } = this.props;
    if (
      prevProps.request?.query?.query !== this.props.request?.query?.query &&
      this.props.request?.query?.query !== ''
    ) {
      if (!newDesignLuxe && pageType !== 'pdp') {
        this.triggerRemainingImpressionsOnSortOrFilter();
      }
      if (msiteLuxe || newDesignLuxe || pageType === 'pdp') {
        this.sendRemainingImpressions();
        setTimeout(() => {
          deviceType === 'mobile'
            ? this.luxeSetDefaultProductImpression()
            : this.luxeSetDefaultDesktopProductImpression();
        }, 1000);
      }
    }
    if (prevProps.request?.search !== this.props.request?.search) {
      if (msiteLuxe || newDesignLuxe || pageType !== 'pdp') {
        this.setState({ wishlistOptionCodeUpdates: false });
        setTimeout(() => {
          this.setState({ wishlistOptionCodeUpdates: true });
        }, 500);
      }
    }
    const { width = 320 } = elementSize(this.gridContainer) || {};
      if (width !== this.props.gridWidth && width !== 0 && this.props.deviceType === 'mobile') {
        this.props.setGridConfig({ gridWidth: width });
      }
  }

  setGridScroll() {
    const { deviceType, pageType } = this.props;
    if (deviceType === 'mobile' && pageType === 'plp') {
      setTimeout(() => {
        this.checkGridScrolling();
      }, 2000);
    }
  }

  checkPageLoadedViaCache(event, eventType) {
    if (event.persisted) {
      this.setGridScroll();
    }
  }

  checkGridScrolling = () => {
    const pdpScrollPosition = readCookie('mobilePLPScrollPosition');
    if (pdpScrollPosition > 0) {
      const currentPosition = window.scrollY || 0;
      const startPosition = currentPosition - 20;
      const endPosition = currentPosition + 20;
      if (pdpScrollPosition < startPosition || pdpScrollPosition > endPosition) {
        window.scrollTo(0, pdpScrollPosition);
      }
    }
    eraseCookie('mobilePLPScrollPosition');
  };

  trackScrolling = () => {
    let { showLoadMoreButton = true, loadingMoreItems, isPDP } = this.props;
    const wrappedElement = this.gridContainer;
    if (this.isBottom(wrappedElement) && showLoadMoreButton && !loadingMoreItems) {
      if (isPDP) {
        this.props.loadingMoreItemsOnScrollPDP(true);
      } else {
        this.props.loadingMoreItemsOnScroll(true);
      }
      this.props.loadMoreItems();
    }
  };

  isBottom(el) {
    if (!el) return false;
    return el.getBoundingClientRect().bottom - 2000 <= window.innerHeight;
  }

  componentWillUnmount() {
    // Clean up the IntersectionObserver
    if (this.observer && this.sentinelRef.current) {
      this.observer.unobserve(this.sentinelRef.current);
    }

    if (this.pageshowHandler) {
      window.removeEventListener('pageshow', this.pageshowHandler, { passive: true });
    }
    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler, { passive: true });
    }
    window.removeEventListener('beforeunload', this.beforeUnloadHandler, { passive: true });
    const isTrendWidget = new URLSearchParams(window.location.search).get('isTrendWidget') === 'true';
    if(isTrendWidget){
      const { productClicked } = this.state;
      if (this?.productImpressionData?.products?.length > 0 && !productClicked) {
        this.sendRemainingImpressions();
        this.productImpressionData.products = [];
      }
    }
  }

  luxeSetDefaultDesktopProductImpression() {
    const { entities = {}, results = [], gridColumns } = this.props;
    if (results && results.length > 0) {
      const viewedProducts = results.slice(0, gridColumns);
      this.productImpressionData.products = viewedProducts;
      this.productImpressionData.lastPushedAt = 1;
    }
  }

  luxeSetDefaultProductImpression() {
    const { entities = {}, results = [] } = this.props;
    if (results && results.length > 0) {
      const viewedProducts = results.slice(0, 4);
      this.productImpressionData.products = viewedProducts;
      this.productImpressionData.lastPushedAt = 2;
    }
  }

  /**
   * ⚡ CODE SPLITTING: Use split module for luxe send product impression data
   * Falls back to legacy implementation if modules not loaded
   */
  luxeSendProductImpressionData = (ids, entities, results) => {
     const { enablePlpPerformanceOptimizationsSprint2601 = false, pageCategory } = this.props;
    if (this.splitModules?.luxeSendProductImpressionData) {
      return this.splitModules.luxeSendProductImpressionData(ids, entities, results, {
        ...this.props,
        getSelectedFilterForSegment: this.getSelectedFilterForSegment
      });
    }

    // 🔄 LEGACY: Fallback to original implementation
    const {
      currentStore,
      isWishListPage,
      pageType,
      showPlpExtendedBanner = false,
      toggleView,
      enableGiftWithProduct,
      enablePriorityDeliveryPhase2,
      enableRushDelivery,
      priorityDeliverySLPTagsConfig: { PLP_BUCKET = {} },
      deviceType = '',
      urgencyDriverV2PLPEnable = false
    } = this.props;
    let {
      plp: { screenName = '', screenType = '' }
    } = luxeScreens;
    let listPage = 'PLP Screen';
    let contains_luxe = false;
    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);
    const jioBannerSource = urlParams.get('jioAdsSource');
    const sourcePage = urlParams.get('sourcePage');
    const plp_source_details = jioBannerSource
      ? jioBannerSource.replaceAll('[ccb]', Date.now())
      : false;
    const { pageType: fromPage = '' } = getPageInfo(location.href);
    const isPLPSource = sourcePage || false;
    if (isWishListPage) {
      screenName = luxeScreens.wishlist.screenName || '';
      screenType = luxeScreens.wishlist.screenType || '';
      listPage = 'Closet Screen';
    }
    if (pageType === 'pdp') {
      screenName = luxeScreens.pdp.screenName || '';
      screenType = luxeScreens.pdp.screenType || '';
      listPage = luxeScreens.pdpList.youMayAlsoLikeList || '';
    }
    const fromVideoBanner = redirectedFromVideoBanner();
    const fromFloatingWidget = redirectedFromFloatingWidget();
    const products = ids.reduce((acc, id, index) => {
      const product = entities[id];
      if (product) {
        const {
          name,
          code = '',
          fnlColorVariantData: { brandName = '', colorGroup = '', deliverySlaTag = '' } = {},
          fnlProductData: { planningCategory = '' } = {},
          wasPriceData,
          price: { value: productPrice = '' } = {},
          brickName,
          brickCategory,
          isExclusive,
          productTag,
          sourceStoreId,
          catalog = '',
          catalogName = '',
          segmentId = '',
          experimentId = '',
          isGiftWithPurchase = false,
          offerPrice: { giftAvailable = false } = {},
          productListType = '',
          tags: { categoryTags = [] } = {},
          urgencyDriverTagResponse: { positions = [] } = {}
        } = product;
        let productId = code && code.length > 9 ? code.substring(0, 9) : code;
        let tag = isExclusive ? 'Exclusive' : productTag;
        let priceValue = wasPriceData && wasPriceData.value ? wasPriceData.value : 0;
        let colorValue =
          colorGroup && colorGroup?.includes('_') ? colorGroup.split('_')[1] || '' : colorGroup;
        const discount =
          Number.isInteger(priceValue) && Number.isInteger(productPrice)
            ? priceValue - productPrice
            : '';
        const productCategory = planningCategory || brickCategory || '';
        const dimension60Data =
          Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
        let gwpDimension = '';

        if (enableGiftWithProduct) {
          if (pageType === 'wishlist' && isGiftWithPurchase) {
            gwpDimension = 'GetFreebies';
          } else if (giftAvailable) {
            gwpDimension = 'GetFreebies';
          }
        }

        if (enableRushDelivery || enablePriorityDeliveryPhase2) {
          gwpDimension = gwpDimension
              ? `${gwpDimension}, ${PLP_BUCKET[`${deliverySlaTag}`]}`
              : PLP_BUCKET[`${deliverySlaTag}`];
        }

        let productTagEngineValue = '';
        if (product?.tags?.categoryTags || (positions || []).length) {
          const PLPTagV2TopLeft = getPLPTagV2FromPosition(positions, 'PRODUCT_TILE_IMAGE_TOP_LEFT');
          const newProductTagEngineValue =
            deviceType === 'mobile' &&
            pageType !== 'wishlist' &&
            urgencyDriverV2PLPEnable &&
            PLPTagV2TopLeft?.shortText;

          productTagEngineValue =
            newProductTagEngineValue || getTagValueFromTagEngine(categoryTags);
        }

        acc.push({
          name,
          id: productId,
          price: productPrice,
          brand: brandName,
          category: productCategory,
          list: fromFloatingWidget
            ? 'video floating widget banner plp'
            : fromVideoBanner
            ? 'video banner plp'
            : isPLPSource || listPage, // example only,
          position: results.indexOf(id),
          dimension6: name, // pass value for product_name
          dimension7: productId, // pass value for product_id
          dimension8: brandName, // pass value for product_brand
          dimension9: priceValue, // pass value for product_price
          dimension10: colorValue, // pass value for product_color
          dimension11: '', // pass value for product_size
          dimension12: discount, // pass value for product_trade_discount
          dimension13: productCategory, // pass value for product_category
          dimension14: brickName, // pass value for product_brick
          dimension15: tag || '', // pass value for product_tag
          dimension23: productListType
          ? `${catalogIdToStoreName(catalog || catalogName)}|${productListType}`
          : catalogIdToStoreName(catalog || catalogName),
          dimension16: isWishListPage ? sourceStoreId : currentStore, // pass value for Store_type
          dimension41:
            pageType !== 'wishlist' && toggleView ? (toggleView == 1 ? 'list' : 'grid') : '',
          dimension60: dimension60Data,
          dimension89: tag,
          dimension90: '',
          dimension91: '',
          dimension82: gwpDimension,
          sticker_name: productTagEngineValue
        });
        if (isWishListPage && sourceStoreId === currentStore) {
          contains_luxe = true;
        }
      }
      return acc;
    }, []);
    luxeGtmProductImpressions({
      impressions: products,
      list: fromVideoBanner ? 'video banner plp' : listPage,
      store_type: currentStore,
      page_type: screenType,
      contains_luxe: isWishListPage ? contains_luxe : true,
      showPlpExtendedBanner,
      ProductArray: products,
      plp_source_details,
      plp_source: isPLPSource,
      bottomCardFilterValue: this.getSelectedFilterForSegment(),
      pageCategory: pageCategory
    });
  };

  /**
   * ⚡ CODE SPLITTING: Use split module for luxe track impressions
   * Falls back to legacy implementation if modules not loaded
   */
  luxeTrackImpressions = (
    rowHeightOfGrid,
    results,
    productImpressionData,
    entities,
    sendImpressionData
  ) => {
    if (this.splitModules?.createLuxeTrackImpressions) {
      return this.splitModules.createLuxeTrackImpressions(
        rowHeightOfGrid,
        results,
        productImpressionData,
        entities,
        sendImpressionData
      );
    }

    // 🔄 LEGACY: Fallback to original implementation
    return (scrollEventData = {}) => {
      const { scrollTop = 0 } = scrollEventData;
      const { lastPushedAt = 0, products = [] } = productImpressionData;
      const scrollTopOffset = 2 * rowHeightOfGrid;

      const pushCheckPoint = Math.floor((scrollTop + scrollTopOffset) / rowHeightOfGrid);
      if (lastPushedAt === 0 && pushCheckPoint !== 2) {
        this.productImpressionData.lastPushedAt = pushCheckPoint;
      } else if (pushCheckPoint > 1 && pushCheckPoint > lastPushedAt) {
        const start = lastPushedAt * 2;
        const stop = pushCheckPoint * 2;
        const viewedProducts = results.slice(start, stop);
        const temporaryStagedProducts = [...products, ...viewedProducts];
        if (temporaryStagedProducts.length <= 9) {
          this.productImpressionData.products = temporaryStagedProducts;
        } else {
          const chunks = chunk(temporaryStagedProducts, 10);
          this.productImpressionData.products = chunks.reduce((acc, chunkArr) => {
            if (chunkArr.length === 10) {
              this.luxeSendProductImpressionData(chunkArr, entities, results);
              return acc;
            }
            return Array.isArray(chunkArr) ? chunkArr : [];
          }, []);
        }
        this.productImpressionData.lastPushedAt = pushCheckPoint;
      }
    };
  };

  /**
   * ⚡ CODE SPLITTING: Use split module for luxe track desktop impressions
   * Falls back to legacy implementation if modules not loaded
   */
  luxeTrackDesktopImpressions = (
    rowHeightOfGrid,
    results,
    productImpressionData,
    entities,
    sendImpressionData
  ) => {
    if (this.splitModules?.createLuxeTrackDesktopImpressions) {
      const { gridColumns } = this.props;
      return this.splitModules.createLuxeTrackDesktopImpressions(
        rowHeightOfGrid,
        results,
        productImpressionData,
        entities,
        sendImpressionData,
        gridColumns
      );
    }

    // 🔄 LEGACY: Fallback to original implementation
    return (scrollEventData = {}) => {
      const { scrollTop = 0 } = scrollEventData;
      const { lastPushedAt = 0, products = [] } = productImpressionData;
      const { gridColumns } = this.props;
      let adjustedRowHeight = rowHeightOfGrid;
      if (gridColumns === 3) adjustedRowHeight = 535;
      const scrollTopOffset = 2 * adjustedRowHeight;

      const pushCheckPoint = Math.floor((scrollTop + scrollTopOffset) / adjustedRowHeight);
      if (lastPushedAt === 0 && pushCheckPoint !== 1) {
        this.productImpressionData.lastPushedAt = pushCheckPoint;
      } else if (pushCheckPoint > 1 && pushCheckPoint > lastPushedAt) {
        const start = lastPushedAt * gridColumns;
        const stop = pushCheckPoint * gridColumns;
        const viewedProducts = results.slice(start, stop);
        const temporaryStagedProducts = [...products, ...viewedProducts];
        if (temporaryStagedProducts.length <= 9) {
          this.productImpressionData.products = temporaryStagedProducts;
        } else {
          const chunks = chunk(temporaryStagedProducts, 10);
          this.productImpressionData.products = chunks.reduce((acc, chunkArr) => {
            if (chunkArr.length === 10) {
              this.luxeSendProductImpressionData(chunkArr, entities, results);
              return acc;
            }
            return Array.isArray(chunkArr) ? chunkArr : [];
          }, []);
        }
        this.productImpressionData.lastPushedAt = pushCheckPoint;
      }
    };
  };

  loadWishlistDataInCard() {
    this.props.loadWishlistOptionCodesAPI();
    setTimeout(() => {
      this.setState({ wishlistOptionCodeUpdates: true });
    }, 2000);
  }

  hitWishlistOptionCodesAPI() {
    console.log('HITTING WISHLIST OPTIONSCODE API');
    const { username } = this.props;
    if (!username) return false;
    const wishlistData = JSON.parse(localStorage.getItem('productWishlistStatusData'));
    if (!wishlistData) {
      this.loadWishlistDataInCard();
    } else {
      const userWishlistData = wishlistData[username];
      if (!userWishlistData) {
        this.loadWishlistDataInCard();
        return false;
      }
      const { updatedOn = '' } = userWishlistData;
      if (Date.now() - updatedOn >= 86400000) {
        this.loadWishlistDataInCard();
      }
    }
  }
}

// ⚡ PERFORMANCE OPTIMIZATION: Memoized Selectors
// These selectors only recompute when their dependencies change
// This prevents expensive calculations on every Redux state update
// Memoized selectors prevent expensive calculations on every Redux state update

const getJioAdsState = state => state.jioAds;
const getWidgetsState = state => state?.widgets?.PLP?.widgetsData?.data?.widgets || [];

// Memoized: Filter listing ads by grid columns
const getFilteredListingAds = createSelector(
  [getJioAdsState, state => state.grid.gridColumns],
  (jioAds, gridColumns) => {
    const { listingAds = [] } = jioAds;
    const jioAdsABFVarientFlag = '1';

    if (jioAdsABFVarientFlag === '1' && gridColumns < 3) {
      // prettier-ignore
      return listingAds.filter(
        ads => ads.customimage && ads.customimage.length > 0 && ads.customimage[0][bannerAdViewType.grid]
      );
    }
    if (jioAdsABFVarientFlag === '2' && gridColumns < 3) {
      // prettier-ignore
      return listingAds.filter(
        ads => ads.customimage && ads.customimage.length > 0 && ads.customimage[0][bannerAdViewType.list]
      );
    }
    if (gridColumns >= 3) {
      // prettier-ignore
      return listingAds.filter(
        ads => ads.customimage && ads.customimage.length > 0 && ads.customimage[0][bannerAdViewType.desktop]
      );
    }
    return listingAds;
  }
);

// Memoized: Insert ads into grid results
const getGridWithAds = createSelector(
  [
    state => state.grid.gridResults,
    state => state.grid.gridColumns,
    getFilteredListingAds,
    state => getjioAdStartPositionMSite(state),
    state => getjioAdsEndPositionMsite(state),
    state => getjioAdsFrequencyMsite(state),
    (state, ownProps) => ownProps.changeView
  ],
  (
    gridResults,
    gridColumns,
    listingAds,
    jioAdStartPosition,
    jioAdsEndPosition,
    jioAdsFrequency,
    changeView
  ) => {
    const jioAdsABFVarientFlag = '1';
    const gc = changeView ? 1 : 2;

    if (jioAdsABFVarientFlag === '1' && gridColumns < 3 && listingAds.length > 0) {
      return getUpdatedGridWithJioAds(
        gridResults,
        jioAdStartPosition - 1,
        jioAdsEndPosition - 1,
        jioAdsFrequency,
        listingAds.length,
        gc
      );
    }
    return gridResults;
  }
);

// Memoized: Insert widgets into grid
const getGridWithWidgets = createSelector(
  [getGridWithAds, getWidgetsState, state => state.grid.gridColumns],
  (gridResults, widgets, gridColumns) => {
    if (gridColumns === 2 && widgets.length > 0) {
      return insertWidgetsInGrid(gridResults, widgets);
    }
    return gridResults;
  }
);

// Memoized: Insert InlineFilters into grid (for mobile 2-column grids)
const getGridWithInlineFilters = createSelector(
  [
    getGridWithWidgets,
    state => state.grid.gridColumns,
    state => getCurrentStore(state),
    state => getVisualFiltersConfig(state),
    state => state.facets?.inlineFilters,
    state => getTrendBasedDiscoveryConfig(state)
  ],
  (
    gridResults,
    gridColumns,
    currentStore,
    visualFiltersConfig,
    inlineFilters,
    trendBasedDiscoveryFlag
  ) => {
    const { master: enableVisualFilters = false, inlineFilter = {} } = visualFiltersConfig || {};
    const visualFilterConfigForStore = visualFiltersConfig?.stores?.[(currentStore || '')?.toLowerCase()] || visualFiltersConfig?.stores?.['ajio'] || {};
    const { master: visualFilterForStoreEnabled = false } = visualFilterConfigForStore || {};
    const { position = 6, startPosition = 5 } = inlineFilter || {};
    const isVisualFilterEnabled = enableVisualFilters && visualFilterForStoreEnabled;
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const isTrendWidget = urlParams.get('isTrendWidget') || "";
    const isTrendBasedDiscovery = trendBasedDiscoveryFlag?.master && isTrendWidget === "true";
    if (
      gridColumns === 2 &&
      isVisualFilterEnabled &&
      visualFilterConfigForStore?.inlineFilterEnabled &&
      inlineFilters?.length > 0 &&
      !isTrendBasedDiscovery
    ) {
      return insertInlineFiltersInGrid(gridResults, position, gridColumns, startPosition);
    }
    return gridResults;
  }
);

const mapStateToProps = (state, ownProps) => {
  let {
    grid = {},
    gridPDP = {},
    request = {},
    loaders = {},
    metaData = {},
    flags,
    productsAdditionalInfo = {},
    product: { reqObj = {} } = {},
    jioAds: { listingAds = [] } = {},
    facets = {}
  } = state;
  const currentStore = getCurrentStore(state);

  const visualFiltersConfig = getVisualFiltersConfig(state);
  const { master: enableVisualFilters = false, inlineFilter = {} } = visualFiltersConfig || {};
  const { position = 6, startPosition = 5 } = inlineFilter || {};
  const visualFilterConfigForStore = visualFiltersConfig?.stores?.[(currentStore || '')?.toLowerCase()] || visualFiltersConfig?.stores?.['ajio'] || {};
  const { master: visualFilterForStoreEnabled = false } = visualFilterConfigForStore || {};
  const isVisualFilterEnabled = enableVisualFilters && visualFilterForStoreEnabled;
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const isTrendWidget = urlParams.get('isTrendWidget') || "";
  const trendBasedDiscoveryFlag = getTrendBasedDiscoveryConfig(state);
  const isTrendBasedDiscovery = trendBasedDiscoveryFlag?.master && isTrendWidget === "true";

  const widgets = state?.widgets?.PLP?.widgetsData?.data?.widgets || [];
  let isPDP = false;
  const { location = {} } = window;

  if (location?.pathname?.includes('/p/') && reqObj.deviceType === 'mobile') {
    grid = gridPDP;
    request = reqObj;
    isPDP = true;
  }

  const { totalResults = 0, deviceType = '', browserPath, freeTextSearch } = request;
  const showProductsLoader = !!loaders['products-loader'];
  const { priceRevealMetaInfo = {}, pagination = {}, metaElementData = [] } = productsAdditionalInfo;
  const Url = metaElementData.reduce((acc, item) => {
    const { name = '', content = '' } = item;
    if (name === 'pageUrl') {
      return content;
    }
    return acc;
  }, '');

  let {
    entities,
    gridColumnWidth,
    gridRowHeight,
    gridWidth,
    gridColumns,
    results = [],
    loadingMoreItems
  } = grid;

  let gridResults = getGridWithWidgets(state, ownProps);
  let gridRowCount = gridResults.length;
  listingAds = getFilteredListingAds(state, ownProps);
  const enablePlpPerformanceOptimizationsSprint2601 =
    getAmplitudeExperimentVariant('perf_26_01_tbt_flag', state) || false;
  // const enablePlpPerformanceOptimizationsSprint2601 = true;

  // ⚡ OPTIMIZED: Use memoized selectors (only recompute when dependencies change)
  if (enablePlpPerformanceOptimizationsSprint2601) {
    gridResults = getGridWithInlineFilters(state, ownProps);
    gridRowCount = gridResults.length;
    listingAds = getFilteredListingAds(state, ownProps);
  } else {
    // 🔄 LEGACY: Original implementation (compute every time)
    gridResults = grid.gridResults || [];
    const gc = ownProps.changeView ? 1 : 2;
    const jioAdsFrequencyMsite = getjioAdsFrequencyMsite(state) - 1;
    const jioAdStartPositionMSite = getjioAdStartPositionMSite(state) - 1;
    const jioAdsEndPositionMsite = getjioAdsEndPositionMsite(state) - 1;
    const jioAdsABFVarientFlag = '1';

    if (jioAdsABFVarientFlag === '1' && gridColumns < 3) {
      // prettier-ignore
      listingAds = listingAds.filter(
        ads => ads.customimage && ads.customimage.length > 0 && ads.customimage[0][bannerAdViewType.grid]
      );
      gridResults = getUpdatedGridWithJioAds(
        gridResults,
        jioAdStartPositionMSite,
        jioAdsEndPositionMsite,
        jioAdsFrequencyMsite + 1,
        listingAds.length,
        gc
      );
    } else if (jioAdsABFVarientFlag === '2' && gridColumns < 3) {
      // prettier-ignore
      listingAds = listingAds.filter(
        ads => ads.customimage && ads.customimage.length > 0 && ads.customimage[0][bannerAdViewType.list]
      );
    } else if (gridColumns >= 3) {
      // prettier-ignore
      listingAds = listingAds.filter(
        ads => ads.customimage && ads.customimage.length > 0 && ads.customimage[0][bannerAdViewType.desktop]
      );
    }

    if (gridColumns === 2 && widgets.length > 0) {
      gridResults = insertWidgetsInGrid(gridResults, widgets);
    }

    // Insert InlineFilters rows for mobile (2-column grid) after every 5 rows
    if (gridColumns === 2 && isVisualFilterEnabled && visualFilterConfigForStore?.inlineFilterEnabled && facets?.inlineFilters?.length > 0 && !isTrendBasedDiscovery) {
      gridResults = insertInlineFiltersInGrid(gridResults, position, gridColumns, startPosition);
    }

    gridRowCount = gridResults.length;
  }

  const { currentPage = 0, totalPages = 1 } = pagination || {};
  const showLoadMoreButton = currentPage < totalPages - 1;
  const { isDesktopThemeEnable, isThemeEnable } = getLuxeFlags(flags);

  const newDesignLuxe = isDesktopThemeEnable === 'ON' && currentStore === 'luxe';
  const msiteLuxe = isThemeEnable === 'ON' && currentStore === 'luxe';
  const isWishListPage = browserPath ? !!browserPath.includes('wishlist') : false;

  const jioAdsRowCount = getjioAdsRowCount(state);
  const jioAdsStartRowDesktop = getjioAdsStartRowDesktop(state);
  const jioAdsEndRowDesktop = getjioAdsEndRowDesktop(state);
  const jioAdsFrequencyMsite = getjioAdsFrequencyMsite(state) - 1;
  const jioAdStartPositionMSite = getjioAdStartPositionMSite(state) - 1;
  const jioAdsEndPositionMsite = getjioAdsEndPositionMsite(state) - 1;
  const showAdTag = getShowAdTagBannerListingAds(state);
  const jioAdsABFVarientFlag = '1';

  const username = signInCheck(true);
  const plpUnratedProdWidgetPosition = getPLPUnratedProductsWigetPosition(state);
  const wishlistUnratedProdWidgetPosition = getWishlistUnratedProductsWidgetPosition(state);
  const unratedItems = getUnratedItems(state);
  const enableRating = getEnableRating(flags, state);
  const enableRatingLabelPLP = getEnableRatingLabelPLP(state);
  const enableRatingLabelWishlist = getEnableRatingLabelWishlist(state);
  const enableGiftWithProduct = getGiftWithProductEnable(state.flags);
  let ratingConflictWithBanner = false;
  const previousSource = getPreviousSearchSource(state);
  const pageCategory = getPageCategory(state);
  const isPlpBannerOsmosEnabled  = getAmplitudeExperimentVariant('enable-plp-banners-plp-osmos', state);
  const enablePlaOnPLP = getAmplitudeExperimentVariant('enable-pla-on-plp-osmos', state);

  const osmosSpecs = getAmplitudeVariantPayload('osmosadsspecs', state, {});
  const osmosClientId = osmosSpecs?.clientId || '';

  // condition to check conflict of banner ads and rating widget
  if (listingAds.length) {
    gridResults.forEach((e, i) => {
      const [p1, p2] = e;
      if (p1 && p2) {
        if (p1.includes('ad') || p2.includes('ad')) {
          if (i === plpUnratedProdWidgetPosition) ratingConflictWithBanner = true;
        }
      }
    });
  }

  return {
    newDesignLuxe,
    gridResults,
    entities,
    gridColumnWidth,
    gridRowHeight,
    gridRowCount,
    gridWidth,
    gridColumns,
    request,
    showLoadMoreButton,
    showProductsLoader,
    deviceType,
    results,
    loadingMoreItems,
    metaData,
    currentStore,
    msiteLuxe,
    isWishListPage,
    username,
    flags,
    priceRevealMetaInfo,
    isPDP,
    pageUrl: Url,
    freeTextSearch,
    // desktop banner ads params
    jioAdsRowCount,
    jioAdsStartRowDesktop,
    // mobile banner ads params
    jioAdsEndPositionMsite,
    jioAdStartPositionMSite,
    jioAdsFrequencyMsite,
    jioAdsEndRowDesktop,
    listingAds,
    jioAdsABFVarientFlag,
    plpUnratedProdWidgetPosition,
    wishlistUnratedProdWidgetPosition,
    unratedItems,
    enableRating,
    enableRatingLabelPLP,
    enableRatingLabelWishlist,
    ratingConflictWithBanner,
    showAdTag,
    facets,
    masterGA3: getMasterGA3Flag(state) === 'ON',
    masterGA4: getMasterGA4Flag(state) === 'ON',
    releaseGA4: getReleaseGA4Flag(state) === 'ON',
    enableGiftWithProduct,
    previousSource,
    pageCategory,
    enableRushDelivery: getRushDeliveryFlag(state),
    enablePriorityDeliveryPhase2: getPriorityDeliveryPhase2Flag(state.flags),
    priorityDeliverySLPTagsConfig: getPriorityDeliverySLPTagsConfig(state),
    ajiogramInterventionsConfig: getAjiogramInterventionsConfig(state?.flags),
    widgets,
    plpLoadMargin: getPLPProductLoadMargin(state?.flags),
    urgencyDriverV2PLPEnable: getUrgencyDriverV2PLPEnable(state),
    isPlpBannerOsmosEnabled,
    enablePlaOnPLP,
    osmosClientId,
    visualFiltersConfig,
    ...ownProps
  };
};

function mapDispatchToProps(dispatch) {
  return {
    setGridConfig: config => {
      dispatch(setGridConfig(config));
    },
    loadingMoreItemsOnScroll: loadingItems => {
      dispatch(loadingMoreItemsOnScroll(loadingItems));
    },
    setGridConfigPDP: config => {
      dispatch(setGridConfigPDP(config));
    },
    loadingMoreItemsOnScrollPDP: loadingItems => {
      dispatch(loadingMoreItemsOnScrollPDP(loadingItems));
    },
    loadWishlistOptionCodesAPI: () => {
      loadWishlistOptionCodesAPI();
    },
    fetchUnratedRatingProducts: () => dispatch(fetchUnratedRatingProducts())
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductGrid);
